import { createContext, useContext, useEffect } from "react";
import i18n from "i18next";

import { APIErrorResponse } from "shared/api/api";
import { UserInfo } from "shared/api/info/api";
import { useUserInfo } from "shared/api/info/hooks";
import { GeneralConfigModel } from "shared/api/superadmin/api";
import {
  useAdminPagesConfig,
  useGeneralConfig,
} from "shared/api/superadmin/hooks";

import { DEFAULT_GENERAL_CONFIG } from "pages/SuperAdmin/constants";
import { PagesConfigModel } from "pages/SuperAdmin/PagesConfig/types";
import { filterToValidPageConfigEntries } from "pages/SuperAdmin/PagesConfig/utils";

import { USER_LANG_KEY } from "features/ui/LanguageSelect";

import { ContextWrapComponentProps } from "./types";

interface ConfigContextInterface {
  general: GeneralConfigModel;
  pages: PagesConfigModel;
  userInfo: UserInfo;
  loaded: boolean;
  error?: APIErrorResponse;
}

const defaultGeneralConfig: GeneralConfigModel = DEFAULT_GENERAL_CONFIG;
const defaultPagesConfig: PagesConfigModel = {};
const defaultUserInfo: UserInfo = {
  isAdmin: false,
  isSuperAdmin: false,
};

const ConfigContext = createContext<ConfigContextInterface>({
  general: defaultGeneralConfig,
  pages: defaultPagesConfig,
  userInfo: defaultUserInfo,
  loaded: false,
});

const ConfigContextWrapper = ({ children }: ContextWrapComponentProps) => {
  const { data: generalConfig, error: generalConfigError } = useGeneralConfig(
    {}
  );
  const { data: pagesConfigArray, error: pagesConfigError } =
    useAdminPagesConfig({});
  const { data: userInfo } = useUserInfo();

  // Strip stale entries so removed keys can't silently drive runtime behavior
  // if the same key is later reused by a new feature. See SVC-3886.
  const pagesConfig: PagesConfigModel | undefined = pagesConfigArray
    ? filterToValidPageConfigEntries(pagesConfigArray).reduce(
        (acc, { ID, config }) => {
          acc[ID] = config;

          return acc;
        },
        {} as PagesConfigModel
      )
    : undefined;

  const genConfig = generalConfig || defaultGeneralConfig;

  useEffect(() => {
    if (generalConfig?.languageCode) {
      const userLang = localStorage.getItem(USER_LANG_KEY);
      if (!userLang) {
        const i18nCode = generalConfig.languageCode.split("-")[0];
        if (i18n.language !== i18nCode) {
          i18n.changeLanguage(i18nCode);
        }
      }
    }
  }, [generalConfig?.languageCode]);

  return (
    <ConfigContext.Provider
      value={{
        general: genConfig || defaultGeneralConfig,
        pages: pagesConfig || defaultPagesConfig,
        userInfo: userInfo || defaultUserInfo,
        loaded: Boolean(generalConfig) && Boolean(pagesConfig),
        error: generalConfigError || pagesConfigError,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfigContext = (): ConfigContextInterface => {
  const context = useContext(ConfigContext);

  if (!context) {
    throw new Error("useConfigContext must be used within a ConfigContext");
  }

  return context;
};

export default ConfigContextWrapper;
